import React, { useMemo } from "react";
import {
  CalendarDays,
  ChevronRight,
  ChevronRightIcon,
  Info,
  SquareArrowUpRight,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shadcnComponents/ui/table";
import { cn } from "@/lib/utils";
import { DraftColInfluDetails } from "./LiveCampaignDraftComponents";
import { Button } from "../../../../../../shadcnComponents/ui/button";
import { LoadingButton } from "../../../../../../shadcnComponents/ui/loading-button";
import { BiSync } from "react-icons/bi";
import { Preview } from "../../CustomHooks/LinkedinEditor";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../../../shadcnComponents/ui/tooltip";
import { format, formatDistanceToNow } from "date-fns";
import { calculatePostDate } from "../../../../../../Helpers/format-helper";
import mixpanel from "mixpanel-browser";
import { SelectedInfluencer } from "../../CampaignInterface/LiveCampaignInterface";

// interface Influencer {
//   id: string;
//   postTracking: {
//     impressions: number;
//     likes: number;
//     comments: number;
//     reposts: number;
//     postContent: string;
//     postCreatedOn: string;
//     updatedAt: string;
//     impressionsUpdatedAt: string;
//   };
//   influencerDetails: {
//     name: string;
//     profile: string;
//     tagline: string;
//   };
//   postAnalysisReportUrl: string;
//   trackingLink: string;
//   _id: string;
// }

interface ExpandableRowProps {
  index: number;
  influencer: SelectedInfluencer;
  isExpanded: boolean;
  onToggle: () => void;
}

function ExpandableRow({
  index,
  influencer,
  isExpanded,
  onToggle,
}: ExpandableRowProps) {
  const postDate = useMemo(() => {
    const postDate = calculatePostDate(influencer?.trackingLink) || null;
    return influencer?.postTracking?.postCreatedOn
      ? new Date(influencer?.postTracking?.postCreatedOn)
      : postDate;
  }, [influencer?.postTracking?.postCreatedOn, influencer?.trackingLink]);

  return (
    <TableRow
      className="hover:bg-muted/50 transition-colors cursor-pointer"
      onClick={onToggle}
    >
      <TableCell className="font-medium">{index + 1}</TableCell>
      <TableCell className="font-medium float-start">
        <DraftColInfluDetails Influencer={influencer} disableProfileLink />
      </TableCell>
      <TableCell className="text-wrap text-[#000] text-center font-[Inter] text-[12px] not-italic font-medium leading-[20px]">
        {postDate ? format(postDate, "dd/MM/yy hh:mm a") : "-"}
      </TableCell>
      <TableCell className="text-[#000] text-center font-[Inter] text-[12px] not-italic font-medium leading-[20px]">
        {influencer?.postTracking?.impressions > 0
          ? influencer?.postTracking?.impressions?.toLocaleString()
          : "-"}
      </TableCell>
      <TableCell className="text-[#000] text-center font-[Inter] text-[12px] not-italic font-medium leading-[20px]">
        {influencer?.postTracking?.likes > 0
          ? influencer?.postTracking?.likes?.toLocaleString()
          : "-"}
      </TableCell>
      <TableCell className="text-[#000] text-center font-[Inter] text-[12px] not-italic font-medium leading-[20px]">
        {influencer?.postTracking?.comments > 0
          ? influencer?.postTracking?.comments?.toLocaleString()
          : "-"}
      </TableCell>
      <TableCell className="text-[#000] text-center font-[Inter] text-[12px] not-italic font-medium leading-[20px]">
        {influencer?.postTracking?.reposts > 0
          ? influencer?.postTracking?.reposts?.toLocaleString()
          : "-"}
      </TableCell>
      {/* status */}
      <TableCell className="text-[#000] text-center font-[Inter] text-[12px] not-italic font-medium leading-[20px]">
        {/* badge */}
        {influencer?.trackingLink ? (
          // Live badge
          <div className="py-1 px-2 rounded-full  border-2 border-green-500 bg-green-50 text-green-500 ">
            Live
          </div>
        ) : influencer?.liveAt ? (
          <span className="py-1 px-2 rounded-full  border-2 border-yellow-500 bg-yellow-50 text-yellow-500 ">
            Scheduled
          </span>
        ) : (
          <span className="py-1 px-2 rounded-full  border-2 border-red-500 bg-red-50 text-red-500 ">
            Not Scheduled
          </span>
        )}
      </TableCell>
      <TableCell className="w-[50px] text-center p-0">
        <div className="flex items-center justify-center py-4">
          <ChevronRight
            className={cn(
              "h-4 w-4 transition-transform duration-200 rotate-90",
              isExpanded && "transform -rotate-90"
            )}
          />
        </div>
      </TableCell>
    </TableRow>
  );
}

interface ExpandedContentProps {
  isExpanded: boolean;
  influencer: SelectedInfluencer;
  collabId: string;
  index: number;
  selectedInfluencers: SelectedInfluencer[];
  handleTrackPost;
  handleViewReport: () => void;
}

interface SyncInfoProps {
  loadingTracking?: boolean;
  title: string;
  timeStamp: string;
  hideSyncBtn?: boolean;
  onClickSync?: () => void;
}

function SyncInfo({
  loadingTracking = false,
  title,
  timeStamp,
  hideSyncBtn,
  onClickSync,
}: SyncInfoProps) {
  return (
    <div className="space-y-2">
      <div className="text-[#899CB1] font-[Inter] text-xs not-italic font-medium leading-[normal]">
        {title}
      </div>
      <div className="flex gap-2 items-center">
        <CalendarDays size={16} color="#3265FE" />
        <span className="text-[#000] text-center font-[Inter] text-[12px] not-italic font-medium leading-[normal]">
          {timeStamp}
        </span>
        {!hideSyncBtn && (
          <button
            className="flex gap-1 items-center rounded-md px-2 py-1 bg-[#F1F5F9] text-[#0F172A] font-[Inter] text-xs not-italic font-medium leading-[normal] hover:bg-[#E7E8F7]"
            onClick={onClickSync}
            disabled={hideSyncBtn}
          >
            <span>{loadingTracking ? "Syncing..." : "Sync Now"}</span>
            <LoadingButton
              variant="ghost"
              size="icon"
              loading={loadingTracking}
              className="p-0 m-0 size-4"
            >
              {!loadingTracking && (
                <>
                  <BiSync size={16} color="#3793FF" title="sync" />
                </>
              )}
            </LoadingButton>
          </button>
        )}
      </div>
    </div>
  );
}

function ExpandedContent({
  isExpanded,
  influencer,
  collabId,
  selectedInfluencers,
  handleTrackPost,
  handleViewReport,
}: ExpandedContentProps) {
  const [loadingLikesSync, setLoadingLikesSync] = React.useState(false);

  const openPostLink = () => {
    window.open(influencer?.trackingLink, "_blank");
  };

  const handleClickSyncLikesNComments = async () => {
    mixpanel.track(
      "Brand Live Campaign Performance: Sync Likes & Comments - clicked",
      {
        "Influencer Name": influencer?.influencerDetails?.name,
        "Collab Id": collabId,
      }
    );
    setLoadingLikesSync(true);
    const InfluencerIndex = selectedInfluencers?.findIndex(
      (i) => i._id === influencer?._id
    );
    await handleTrackPost(
      InfluencerIndex,
      collabId,
      influencer?.trackingLink,
      influencer?._id
    );
    setLoadingLikesSync(false);
    // window.location.reload();
  };

  const viewReport = () => {
    mixpanel.track(
      "Brand Live Campaign Performance: View Post Analysis Report - clicked",
      {
        "Influencer Name": influencer?.influencerDetails?.name,
        "Collab Id": collabId,
      }
    );
    handleViewReport();
  };

  return (
    <TableRow>
      <TableCell colSpan={9} className="p-0 text-start">
        <div
          className={cn(
            "grid transition-all duration-200 ease-in-out",
            isExpanded
              ? "grid-rows-[1fr] opacity-100"
              : "grid-rows-[0fr] opacity-0"
          )}
        >
          <div className="overflow-hidden">
            <div className="p-3.5 flex flex-col gap-3 bg-[#F8F6F6] rounded-lg">
              <div className="flex items-center gap-1">
                <span className="text-[#121212] font-[Inter] text-[14px] not-italic font-semibold leading-[24px]">
                  Post Details
                </span>
                {influencer?.trackingLink && (
                  <SquareArrowUpRight
                    className="text-[#3265FE] size-4 hover:text-red-500 cursor-pointer"
                    onClick={openPostLink}
                  />
                )}
              </div>
              <div className="flex gap-6">
                {influencer?.postTracking?.postContent?.trim()?.length > 0 && (
                  <div className="w-fit bg-white rounded-lg">
                    <Preview
                      content={influencer?.postTracking?.postContent?.replace(
                        "…show more",
                        ""
                      )}
                      viewMode={"desktop"}
                      currentUserName={influencer?.influencerDetails?.name}
                      profileImage={influencer?.influencerDetails?.profile}
                      tagline={influencer?.influencerDetails?.tagline}
                      date={
                        influencer?.postTracking?.postCreatedOn
                          ? formatDistanceToNow(
                              new Date(influencer?.postTracking?.postCreatedOn),
                              { addSuffix: true }
                            )
                          : "-"
                      }
                      likes={influencer?.postTracking?.likes || 0}
                    />
                  </div>
                )}
                <div className="w-1/2 bg-white p-5 rounded-lg flex flex-col justify-between gap-6">
                  <div className="flex flex-col gap-6">
                    <SyncInfo
                      title="Last Impression Sync:"
                      timeStamp={
                        influencer?.postTracking?.impressionsUpdatedAt
                          ? format(
                              new Date(
                                influencer?.postTracking?.impressionsUpdatedAt
                              ),
                              "dd MMM, yy hh:mm a"
                            )
                          : "Not synced yet by the influencer"
                      }
                      hideSyncBtn={true}
                    />
                    <SyncInfo
                      loadingTracking={loadingLikesSync}
                      title="Last Likes & Comments Sync:"
                      timeStamp={
                        influencer?.postTracking?.updatedAt
                          ? format(
                              new Date(influencer?.postTracking?.updatedAt),
                              "dd MMM, yy hh:mm a"
                            )
                          : "Not synced yet"
                      }
                      onClickSync={handleClickSyncLikesNComments}
                    />
                  </div>
                  <div>
                    <Button
                      variant="destructive"
                      className="flex items-center gap-1.5"
                      disabled={
                        influencer?.postAnalysisReportUrl ? false : true
                      }
                      size="sm"
                      onClick={viewReport}
                    >
                      <span>Check Post-Analysis Report</span>
                      <ChevronRightIcon size={16} />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
}

interface ExpandableTableProps {
  Influencers: SelectedInfluencer[];
  collabId: string;
  selectedInfluencers: SelectedInfluencer[];
  handleTrackPost;
  handleViewReport: () => void;
}

export function PerformanceExpandableTable({
  Influencers,
  collabId,
  selectedInfluencers,
  handleTrackPost,
  handleViewReport,
}: ExpandableTableProps) {
  const [expandedRow, setExpandedRow] = React.useState<string | null>(null);

  const toggleRow = (id: string) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="text-nowrap w-[50px] text-start">
            S. No.
          </TableHead>
          <TableHead className="w-[250px]">Influencer</TableHead>
          <TableHead className="w-[100px] text-center">Posted on</TableHead>
          <TableHead className="text-center">
            <div className="flex gap-1 items-center justify-center">
              <span>Member Reach</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info size={14} color="#CEC3C4" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-sm  text-wrap text-left">
                    <p>
                      No. of people who have seen this post, as last synced by
                      the influencer. It doesn't include repeat displays.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </TableHead>
          <TableHead className="text-center">
            <div className="flex gap-1 items-center justify-center">
              <span>Likes</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info size={14} color="#CEC3C4" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>The number of likes on this post, as last synced.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </TableHead>
          <TableHead className="text-center">
            <div className="flex gap-1 items-center justify-center">
              <span>Comments</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info size={14} color="#CEC3C4" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>The number of comments on this post, as last synced.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </TableHead>
          <TableHead className="text-center">
            <div className="flex gap-1 items-center justify-center">
              <span>Reposts</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info size={14} color="#CEC3C4" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      The number of times this post was shared, as last synced.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </TableHead>
          <TableHead className="text-nowrap text-center">Status</TableHead>
          <TableHead className="text-nowrap text-end">View Details</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {Influencers?.map((Influencer, index) => (
          <React.Fragment key={index}>
            <ExpandableRow
              index={index}
              influencer={Influencer}
              isExpanded={expandedRow === index.toString()}
              onToggle={() => toggleRow(index.toString())}
            />
            <ExpandedContent
              influencer={Influencer}
              isExpanded={expandedRow === index.toString()}
              collabId={collabId}
              index={index}
              selectedInfluencers={selectedInfluencers}
              handleTrackPost={handleTrackPost}
              handleViewReport={handleViewReport}
            />
          </React.Fragment>
        ))}
      </TableBody>
    </Table>
  );
}
